import { useCallback, useRef, type FocusEvent, type ReactNode } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import styled from 'styled-components'
import { rem } from 'polished'

export const SlideModalContent = styled.div`
  overflow-x: hidden;
`
export const SlidePanels = styled.div`
  --journey-details-slide-offset: calc(-100% - ${rem(20)});

  display: grid;
  height: 100%;
  grid-template-columns: 100% 100%;
  transform: translateX(0);
  transition: transform 0.3s;
  gap: ${rem(20)};

  >:first-child {
    transition: opacity 0.3s;
    opacity: 1;
  }

  >:nth-child(2) {
    transition: opacity 0.3s;
    opacity: 0;
  }

  &.right {
    transform: translateX(var(--journey-details-slide-offset));

    >:first-child {
      opacity: 0;
    }

    >:nth-child(2) {
      opacity: 1;
    }
  }

  [dir='rtl'] & {
    --journey-details-slide-offset: calc(100% + ${rem(20)});
  }
`

const SlidePanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`

interface SlidePanelProps {
  active: boolean
  children: ReactNode
}

export function SlidePanel({ active, children }: SlidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const lastFocusedElement = useRef<HTMLElement | undefined>(undefined)
  const wasActive = useRef(active)

  const rememberFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    lastFocusedElement.current = event.target
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (active && !wasActive.current) {
      const panel = panelRef.current
      const previousTarget = lastFocusedElement.current
      const target = previousTarget && panel?.contains(previousTarget) ? previousTarget : panel
      target?.focus({ preventScroll: true })
    }
    wasActive.current = active
  }, [active])

  return <SlidePanelContainer
    ref={panelRef}
    inert={!active}
    aria-hidden={!active}
    tabIndex={-1}
    onFocusCapture={rememberFocus}
  >
    {children}
  </SlidePanelContainer>
}
